
/**
 * ================================================================================
 * PÁGINA: Dashboard
 * PROPÓSITO: Visão geral financeira (KPIs, gráficos, transações recentes)
 * ================================================================================
 */
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  AreaChart, Area, LineChart, Line, CartesianGrid, PieChart, Pie
} from 'recharts';
import React, { useEffect, useState } from 'react';
import { useFinance } from '../context/FinanceContext';
import { useTheme } from '../context/ThemeContext';
import { formatCurrency, formatCurrency as formatCurrencyRaw, formatCompact, calcChange } from '../utils/formatCurrency';
import {
  TrendingUp, TrendingDown, Wallet, PiggyBank, ArrowUpRight, ArrowDownLeft,
  ArrowLeftRight, Clock, Radio
} from 'lucide-react';

import { format } from 'date-fns';
import { pt } from 'date-fns/locale';

// Tooltip customizado para os gráficos
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border-secondary)',
      borderRadius: 12, padding: '12px 16px', boxShadow: 'var(--shadow-card)',
    }}>
      <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 6 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ fontSize: 13, fontWeight: 600, color: p.color }}>
          {p.name}: {formatCurrency(p.value)}
        </p>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const { dashboard, dashboardError, dashboardLoading, fetchDashboard, realtime } = useFinance();
  const { cardShape, chartStyle, chartStyles } = useTheme();
  const [period, setPeriod] = useState('month');
  const [chartPeriod, setChartPeriod] = useState('6m');
  const [categoryType, setCategoryType] = useState('expense');
  const [categoryLimit, setCategoryLimit] = useState(6);

  useEffect(() => {
    fetchDashboard(period, {
      chart_period: chartPeriod,
      category_type: categoryType,
      category_limit: categoryLimit,
    }).catch(() => null);
  }, [period, chartPeriod, categoryType, categoryLimit, fetchDashboard]);

  if (dashboardError && !dashboardLoading && !dashboard) {
    return (
      <div className="card">
        <div className="empty-state">
          <p className="empty-state-title">{dashboardError}</p>
          <button
            className="btn btn-primary"
            onClick={() => fetchDashboard(period, {
              chart_period: chartPeriod,
              category_type: categoryType,
              category_limit: categoryLimit,
            }).catch(() => null)}
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  if (dashboardLoading || !dashboard) {
    return (
      <div>
        <div className="page-header">
          <div>
            <div className="skeleton" style={{ width: 200, height: 28, marginBottom: 8 }} />
            <div className="skeleton" style={{ width: 140, height: 16 }} />
          </div>
        </div>
        <div className="kpi-grid">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="skeleton" style={{ height: 120, borderRadius: 16 }} />
          ))}
        </div>
        <div className="grid-2 mt-lg">
          <div className="skeleton" style={{ height: 320, borderRadius: 16 }} />
          <div className="skeleton" style={{ height: 320, borderRadius: 16 }} />
        </div>
      </div>
    );
  }

  const { net_worth, income, expense, savings, savings_rate, prev_income, prev_expense, wallets, recent_transactions, budgets, goals, upcoming_due, cash_flow_data, category_data } = dashboard;
  const incomeChange = calcChange(income, prev_income);
  const expenseChange = calcChange(expense, prev_expense);

  return (
    <div>
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Visão geral das suas finanças</p>
        </div>
        <div className="tab-bar" style={{ marginBottom: 0 }}>
          {['week', 'month', 'quarter', 'year'].map((p) => (
            <button key={p} className={`tab-item ${period === p ? 'active' : ''}`} onClick={() => setPeriod(p)}>
              {{ week: 'Semana', month: 'Mês', quarter: 'Trimestre', year: 'Ano' }[p]}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: -12, marginBottom: 16 }}>
        <span className={`badge ${realtime?.connected ? 'badge-active' : ''}`} style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: realtime?.connected ? 'var(--accent-green-dim)' : 'var(--bg-input)',
          color: realtime?.connected ? 'var(--accent-green)' : 'var(--text-tertiary)',
        }}>
          <Radio size={12} />
          {realtime?.connected ? 'Tempo real' : 'A reconectar'}
        </span>
      </div>

      {/* KPI Grid */}
      <div className="kpi-grid">
        <div className="kpi-card blue animate-in stagger-1">
          <div className="kpi-label"><Wallet size={16} /> Património Líquido</div>
          <div className="kpi-value">{formatCurrency(net_worth)}</div>
        </div>
        <div className="kpi-card green animate-in stagger-2">
          <div className="kpi-label"><TrendingUp size={16} /> Receitas</div>
          <div className="kpi-value text-income">{formatCurrency(income)}</div>
          {incomeChange && (
            <div className={`kpi-change ${parseFloat(incomeChange) >= 0 ? 'up' : 'down'}`}>
              {parseFloat(incomeChange) >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
              {Math.abs(parseFloat(incomeChange))}% vs período anterior
            </div>
          )}
        </div>
        <div className="kpi-card red animate-in stagger-3">
          <div className="kpi-label"><TrendingDown size={16} /> Despesas</div>
          <div className="kpi-value text-expense">{formatCurrency(expense)}</div>
          {expenseChange && (
            <div className={`kpi-change ${parseFloat(expenseChange) <= 0 ? 'up' : 'down'}`}>
              {parseFloat(expenseChange) <= 0 ? <TrendingDown size={12} /> : <TrendingUp size={12} />}
              {Math.abs(parseFloat(expenseChange))}% vs período anterior
            </div>
          )}
        </div>
        <div className="kpi-card purple animate-in stagger-4">
          <div className="kpi-label"><PiggyBank size={16} /> Poupança</div>
          <div className="kpi-value" style={{ color: savings >= 0 ? 'var(--accent-green)' : 'var(--accent-red)' }}>
            {formatCurrency(savings)}
          </div>
          <div className="kpi-change" style={{ color: 'var(--text-tertiary)' }}>
            Taxa: {savings_rate}%
          </div>
        </div>
      </div>

      {/* Gráficos Row */}
      <div className="grid-2 mt-lg">
        {/* Cash Flow Chart */}
        <div className={`card animate-in stagger-5 variant-${cardShape}`}>
          <div className="section-header">
            <h3 className="section-title">Fluxo de Caixa</h3>
            <select
              className="form-input form-select"
              style={{ width: 130, height: 36, fontSize: 12 }}
              value={chartPeriod}
              onChange={(event) => setChartPeriod(event.target.value)}
            >
              <option value="3m">3 meses</option>
              <option value="6m">6 meses</option>
              <option value="12m">12 meses</option>
              <option value="ytd">Este ano</option>
            </select>
          </div>
          <div className="chart-container" style={{ height: 300, width: '100%', minWidth: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              {chartStyle === chartStyles.MODERN && (
                <BarChart data={cash_flow_data} barGap={4}>
                  <XAxis dataKey="month" tick={{ fill: '#64748B', fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false}
                    tickFormatter={(v) => formatCompact(v)} width={50} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="income" name="Receita" fill="#10B981" radius={[6, 6, 0, 0]} barSize={20} />
                  <Bar dataKey="expense" name="Despesa" fill="#EF4444" radius={[6, 6, 0, 0]} barSize={20} />
                </BarChart>
              )}
              {chartStyle === chartStyles.ELEGANT && (
                <AreaChart data={cash_flow_data}>
                  <defs>
                    <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorExpense" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#EF4444" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="month" tick={{ fill: '#64748B', fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false}
                    tickFormatter={(v) => formatCompact(v)} width={50} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="income" stroke="#10B981" fillOpacity={1} fill="url(#colorIncome)" strokeWidth={2} />
                  <Area type="monotone" dataKey="expense" stroke="#EF4444" fillOpacity={1} fill="url(#colorExpense)" strokeWidth={2} />
                </AreaChart>
              )}
              {chartStyle === chartStyles.MINIMAL && (
                <LineChart data={cash_flow_data}>
                  <XAxis dataKey="month" tick={{ fill: '#64748B', fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false}
                    tickFormatter={(v) => formatCompact(v)} width={50} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="income" stroke="#10B981" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                  <Line type="monotone" dataKey="expense" stroke="#EF4444" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                </LineChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>

        {/* Category Donut */}
        <div className={`card animate-in stagger-6 variant-${cardShape}`}>
          <div className="section-header">
            <h3 className="section-title">{categoryType === 'expense' ? 'Despesas' : 'Receitas'} por Categoria</h3>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <select
                className="form-input form-select"
                style={{ width: 120, height: 36, fontSize: 12 }}
                value={categoryType}
                onChange={(event) => setCategoryType(event.target.value)}
              >
                <option value="expense">Despesas</option>
                <option value="income">Receitas</option>
              </select>
              <select
                className="form-input form-select"
                style={{ width: 96, height: 36, fontSize: 12 }}
                value={categoryLimit}
                onChange={(event) => setCategoryLimit(Number(event.target.value))}
              >
                <option value={3}>Top 3</option>
                <option value={6}>Top 6</option>
                <option value={10}>Top 10</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 20 }}>
            <div style={{ width: 180, height: 180, flexShrink: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={category_data} dataKey="value" nameKey="name" cx="50%" cy="50%"
                    innerRadius={50} outerRadius={80} paddingAngle={3} strokeWidth={0}>
                    {category_data?.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {category_data?.slice(0, 6).map((cat, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <div style={{ width: 10, height: 10, borderRadius: 3, background: cat.color, flexShrink: 0 }} />
                  <span style={{ color: 'var(--text-secondary)', flex: 1 }}>{cat.name}</span>
                  <span className="text-mono" style={{ fontSize: 12, color: 'var(--text-primary)' }}>
                    {formatCurrency(cat.value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid-2 mt-lg">
        {/* Transações Recentes */}
        <div className={`card variant-${cardShape}`}>
          <div className="section-header">
            <h3 className="section-title">Transações Recentes</h3>
          </div>
          {recent_transactions?.slice(0, 7).map((tx) => {
            const isExpense = tx.type === 'expense';
            const isTransfer = tx.type === 'transfer';
            const IconComp = isExpense ? ArrowUpRight : isTransfer ? ArrowLeftRight : ArrowDownLeft;
            const amountColor = isExpense ? 'expense' : isTransfer ? 'transfer' : 'income';

            return (
              <div key={tx.id} className="transaction-item">
                <div className="transaction-icon" style={{
                  background: isExpense ? 'var(--accent-red-dim)' : isTransfer ? 'var(--accent-purple-dim)' : 'var(--accent-green-dim)',
                }}>
                  <IconComp size={20} style={{
                    color: isExpense ? 'var(--accent-red)' : isTransfer ? 'var(--accent-purple)' : 'var(--accent-green)',
                  }} />
                </div>
                <div className="transaction-info">
                  <div className="transaction-desc">{tx.description || tx.category?.name || 'Sem descrição'}</div>
                  <div className="transaction-meta">
                    <span>{tx.category?.name}</span>
                    <span>·</span>
                    <span>{format(new Date(tx.date), 'd MMM', { locale: pt })}</span>
                  </div>
                </div>
                <div className={`transaction-amount ${amountColor}`}>
                  {isExpense ? '-' : isTransfer ? '' : '+'}{formatCurrency(tx.amount)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Metas + Próximos Vencimentos */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Metas */}
          <div className={`card variant-${cardShape}`}>
            <div className="section-header">
              <h3 className="section-title">Metas Activas</h3>
            </div>
            {goals?.slice(0, 3).map((goal) => (
              <div key={goal.id} style={{ marginBottom: 14 }}>
                <div className="flex-between" style={{ marginBottom: 6 }}>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>{goal.name}</span>
                  <span className="text-mono" style={{ fontSize: 13, color: 'var(--accent-blue)' }}>
                    {goal.progress}%
                  </span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill success" style={{ width: `${Math.min(goal.progress, 100)}%` }} />
                </div>
                <div className="flex-between" style={{ marginTop: 4 }}>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{formatCurrency(goal.current_amount)}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{formatCurrency(goal.target_amount)}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Próximos Vencimentos */}
          {upcoming_due && upcoming_due.length > 0 && (
            <div className={`card variant-${cardShape}`}>
              <div className="section-header">
                <h3 className="section-title"><Clock size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />Próximos Vencimentos</h3>
              </div>
              {upcoming_due.map((rec) => (
                <div key={rec.id} className="transaction-item">
                  <div className="transaction-info">
                    <div className="transaction-desc">{rec.name}</div>
                    <div className="transaction-meta">
                      <span>{rec.category?.name}</span>
                      <span>·</span>
                      <span>{format(new Date(rec.next_due), 'd MMM', { locale: pt })}</span>
                    </div>
                  </div>
                  <div className={`transaction-amount ${rec.type}`}>
                    {formatCurrency(rec.amount)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
